import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import posthog from 'posthog-js';
import type { MarketplaceListing, MarketplaceRate } from '../../models/apiTypes';
import { showAlert } from '../../utils/telegram/alert';
import type { useTonConnect } from '../../hooks/useTonConnect';
import { cn } from '../cn';

export interface MarketplaceBuySectionProps {
  buyListing: MarketplaceListing;
  buyRate: MarketplaceRate;
  buyCurrency: 'ton' | 'usdt';
  setBuyCurrency: Dispatch<SetStateAction<'ton' | 'usdt'>>;
  buyWalletLoading: boolean;
  setBuyWalletLoading: Dispatch<SetStateAction<boolean>>;
  showManualPayment: boolean;
  setShowManualPayment: Dispatch<SetStateAction<boolean>>;
  walletConnected: boolean;
  connectWallet: ReturnType<typeof useTonConnect>['connectWallet'];
  sendTon: ReturnType<typeof useTonConnect>['sendTon'];
  sendUsdt: ReturnType<typeof useTonConnect>['sendUsdt'];
  t: TFunction;
}

export default function MarketplaceBuySection({
  buyListing,
  buyRate,
  buyCurrency,
  setBuyCurrency,
  buyWalletLoading,
  setBuyWalletLoading,
  showManualPayment,
  setShowManualPayment,
  walletConnected,
  connectWallet,
  sendTon,
  sendUsdt,
  t,
}: MarketplaceBuySectionProps) {
  return (
    <div className="my-md rounded-[14px] border border-ton-action-alpha-30 bg-[linear-gradient(135deg,var(--color-ton-action-alpha-15),var(--color-ton-action-end-alpha-10))] px-xl py-lg">
      {/* Header */}
      <div className="mb-md flex items-center justify-between">
        <div className="flex items-center gap-sm">
          <span className="text-[16px]">💎</span>
          <span className="[font-family:inherit] text-[14px] font-bold tracking-[0.5px] text-ton-action uppercase">
            {t('For sale')}
          </span>
        </div>
        <div className="rounded-[7px] bg-danger-action px-sm py-[2px] text-[9px] font-bold tracking-[0.3px] text-white">
          NEW
        </div>
      </div>

      {/* Price display */}
      <div className="mb-[10px] flex items-baseline justify-center gap-md">
        <span className="text-[22px] font-bold text-white">
          {parseFloat(buyListing.price_ton.toFixed(4))} TON
        </span>
        <span className="text-[14px] text-text-subtle">
          ≈ ${buyListing.price_usdt.toFixed(2)}
        </span>
      </div>

      {/* Currency selector */}
      <div className="mb-lg flex items-center gap-xs">
        <span className="mr-xs text-[12px] text-text-quieter">{t('Pay with')}:</span>
        <button
          onClick={() => {
            setBuyCurrency('ton');
            posthog.capture('marketplace_currency_selected', {
              currency: 'ton',
              listing_id: buyListing.id,
              house_id: buyListing.map_object_id,
              price_ton: buyListing.price_ton,
              price_usdt: buyListing.price_usdt,
            });
          }}
          className={cn(
            'cursor-pointer rounded-sm px-[14px] py-[5px] text-[13px] font-semibold',
            buyCurrency === 'ton'
              ? 'border border-ton-action bg-ton-action-alpha-20 text-ton-action'
              : 'border border-white-alpha-12 bg-white-alpha-5 text-text-quieter',
          )}
        >
          💎 TON
        </button>
        <button
          onClick={() => {
            setBuyCurrency('usdt');
            posthog.capture('marketplace_currency_selected', {
              currency: 'usdt',
              listing_id: buyListing.id,
              house_id: buyListing.map_object_id,
              price_ton: buyListing.price_ton,
              price_usdt: buyListing.price_usdt,
            });
          }}
          className={cn(
            'cursor-pointer rounded-sm px-[14px] py-[5px] text-[13px] font-semibold',
            buyCurrency === 'usdt'
              ? 'border border-usdt-action bg-usdt-action-alpha-20 text-usdt-action'
              : 'border border-white-alpha-12 bg-white-alpha-5 text-text-quieter',
          )}
        >
          $ USDT
        </button>
      </div>

      {/* Pay with wallet — CTA */}
      <button
        disabled={buyWalletLoading}
        onClick={async () => {
          if (!walletConnected) {
            posthog.capture('marketplace_connect_wallet_prompted', {
              listing_id: buyListing.id,
              house_id: buyListing.map_object_id,
            });
            connectWallet();
            return;
          }
          posthog.capture('marketplace_buy_clicked', {
            listing_id: buyListing.id,
            house_id: buyListing.map_object_id,
            currency: buyCurrency,
            price_ton: buyListing.price_ton,
            price_usdt: buyListing.price_usdt,
            memo: buyListing.memo,
            seller_id: buyListing.seller_id,
          });
          setBuyWalletLoading(true);
          try {
            if (buyCurrency === 'ton') {
              await sendTon({
                to: buyRate.wallet_address,
                amount: buyListing.price_ton.toFixed(4),
                comment: buyListing.memo,
              });
            } else {
              await sendUsdt({
                to: buyRate.wallet_address,
                usdtAmount: buyListing.price_usdt,
                comment: buyListing.memo,
              });
            }
            posthog.capture('marketplace_payment_sent', {
              listing_id: buyListing.id,
              house_id: buyListing.map_object_id,
              currency: buyCurrency,
              price_ton: buyListing.price_ton,
              price_usdt: buyListing.price_usdt,
              memo: buyListing.memo,
              seller_id: buyListing.seller_id,
            });
            showAlert(t('Transaction sent! House will transfer after confirmation.'));
          } catch (err: unknown) {
            const errMessage = err instanceof Error ? err.message : String(err);
            if (errMessage.includes('Interrupted') || errMessage.includes('Cancelled')) {
              posthog.capture('marketplace_payment_cancelled', {
                listing_id: buyListing.id,
                house_id: buyListing.map_object_id,
                currency: buyCurrency,
              });
            } else {
              posthog.capture('marketplace_payment_failed', {
                listing_id: buyListing.id,
                house_id: buyListing.map_object_id,
                currency: buyCurrency,
                error: errMessage,
              });
              showAlert(t('Transaction failed. Try manual payment below.'));
            }
          } finally {
            setBuyWalletLoading(false);
          }
        }}
        className={cn(
          'flex w-full items-center justify-center gap-md rounded-md border-none p-[14px] text-[15px] font-bold text-white',
          buyCurrency === 'ton'
            ? 'bg-[image:var(--gradient-ton-action)]'
            : 'bg-[linear-gradient(90deg,var(--color-usdt-action),var(--color-usdt-action-end))]',
          buyWalletLoading ? 'cursor-default opacity-60' : 'cursor-pointer opacity-100',
        )}
      >
        {buyWalletLoading
          ? t('Processing...')
          : !walletConnected
            ? t('Connect wallet to pay')
            : buyCurrency === 'ton'
              ? `${t('Pay')} ${parseFloat(buyListing.price_ton.toFixed(4))} TON`
              : `${t('Pay')} $${buyListing.price_usdt.toFixed(2)} USDT`}
      </button>

      {/* Divider */}
      <div className="my-lg flex items-center gap-[10px]">
        <div className="h-px flex-1 bg-white-alpha-10" />
        <span className="text-[12px] text-text-muted">{t('or')}</span>
        <div className="h-px flex-1 bg-white-alpha-10" />
      </div>

      {/* Manual payment — collapsible */}
      <button
        onClick={() => setShowManualPayment(v => !v)}
        className="flex w-full cursor-pointer items-center justify-center gap-sm rounded-[10px] border border-white-alpha-10 bg-white-alpha-4 p-[10px] text-[13px] font-medium text-text-quieter"
      >
        {t('Pay manually')}
        <span
          className="text-[10px] [transition:transform_0.2s]"
          style={{
            transform: showManualPayment ? 'rotate(180deg)' : 'rotate(0)',
          }}
        >
          ▼
        </span>
      </button>

      {showManualPayment && (
        <div className="mt-[10px]">
          <div className="mb-[10px] rounded-[10px] bg-black-alpha-30 px-lg py-[10px]">
            <div className="mb-xs text-[11px] text-text-quieter">
              {t('Wallet')}:
            </div>
            <div className="mb-sm break-all text-[12px] text-ton-label">
              {buyRate.wallet_address}
            </div>
            <div className="mb-xs text-[11px] text-text-quieter">
              MEMO:
            </div>
            <div className="text-[14px] font-semibold text-white">
              {buyListing.memo}
            </div>
          </div>
          <div className="flex gap-md">
            <button
              onClick={() => {
                navigator.clipboard.writeText(buyRate.wallet_address);
                showAlert(t('Wallet address copied!'));
              }}
              className="flex-1 cursor-pointer rounded-[10px] border border-ton-action-alpha-30 bg-ton-action-alpha-10 p-[10px] text-[13px] font-semibold text-ton-action"
            >
              {t('Copy wallet')}
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(buyListing.memo);
                showAlert(t('MEMO copied!'));
              }}
              className="flex-1 cursor-pointer rounded-[10px] border border-ton-action-alpha-30 bg-ton-action-alpha-10 p-[10px] text-[13px] font-semibold text-ton-action"
            >
              {t('Copy MEMO')}
            </button>
          </div>
          <div className="mt-md text-[11px] leading-[15px] text-text-muted">
            {t('House transfers automatically after payment is confirmed.')}
          </div>
        </div>
      )}
    </div>
  );
}
